import { Form, Formik } from "formik"
import { useMemo, useState } from "react"
import { Alert, Button, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router"

import DirectionText from "@/components/DirectionText/DirectionText"
import Loading from "@/components/Loading/Loading"
import { useSubmitSurveyMutation } from "@/queries/mutations/surveyMutations"
import { useSingleSurveyData } from "@/queries/queries"
import type { Survey, SurveySubmitData } from "@/types/surveyTypes"

import Question from "./Question"

export default function SurveyAnswerShow() {
  const [currentSection, setCurrentSection] = useState(0)
  const [showWarning, setShowWarning] = useState(false)

  const navigate = useNavigate()
  const { id } = useParams()
  const { t } = useTranslation()

  const surveyQuery = useSingleSurveyData(id!)

  const submitSurveyMutation = useSubmitSurveyMutation(id!)

  const initialValues = useIntialSurveyValues(surveyQuery.data)

  if (surveyQuery.isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <Loading />
      </div>
    )
  }

  if (!surveyQuery.data) {
    return (
      <p className="text-center text-danger">
        {t("surveyAnswerPage.error.data")}
      </p>
    )
  }

  const isAnswered = !!surveyQuery.data.user_answers
  const totalSections = surveyQuery.data.sections?.length || 0

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values) => {
        values.answers = [
          ...values.answers,
          {
            survey_question_id:
              surveyQuery.data.sections?.at(-1)?.questions?.at(-1)?.id || 0,
            answer: values?.note || "",
          },
        ]
        submitSurveyMutation.mutate(values, {
          onSuccess: () => navigate("/surveys"),
        })
      }}
    >
      {({ values }) => {
        const checkSectionAnswered = (sectionIndex: number) =>
          surveyQuery.data.sections?.[sectionIndex].questions?.every((q) => {
            if (q.question_text === "Notes" && q.question_type === "textarea") {
              return true
            }
            return values.answers?.some(
              (ans) =>
                ans.survey_question_id === q.id &&
                ans.answer?.toString().trim() !== ""
            )
          })

        const handleNext = () => {
          if (!checkSectionAnswered(currentSection)) {
            setShowWarning(true)
            return
          }
          setShowWarning(false)
          setCurrentSection((prev) => prev + 1)
        }

        return (
          <Form style={{ maxWidth: "75%", margin: "auto" }}>
            {/* العنوان والوصف */}
            <div>
              <DirectionText as="h1">{surveyQuery.data.title}</DirectionText>
              <DirectionText as="p" className="mb-4">
                {surveyQuery.data.description}
              </DirectionText>
            </div>

            {/* 
              these 2 keys come from the back end
              they represent special sections that are added to every survey
            */}
            {!["Additional Notes", "Recommend EraaSoft"].includes(
              surveyQuery.data?.sections?.[currentSection]?.title || ""
            ) && (
              <Alert
                variant="light"
                className="d-flex justify-content-between fw-bold"
              >
                <p> {`1- ${t("surveyAnswerPage.answerKey.one")}`}</p>
                <p> {`2- ${t("surveyAnswerPage.answerKey.two")}`}</p>
                <p> {`3- ${t("surveyAnswerPage.answerKey.three")}`}</p>
                <p> {`4- ${t("surveyAnswerPage.answerKey.four")}`}</p>
                <p> {`5- ${t("surveyAnswerPage.answerKey.five")}`}</p>
              </Alert>
            )}

            {/* عرض السكشن الحالي */}
            <div className="mb-4">
              <DirectionText as="h5" className="mb-3">
                {surveyQuery.data?.sections?.[currentSection]?.title}
              </DirectionText>
              {surveyQuery.data?.sections?.[currentSection]?.questions?.map(
                (q) => (
                  <Question key={q.id} question={q} isAnswered={isAnswered} />
                )
              )}
            </div>

            {/* Progress */}
            <div className="text-center mb-3">
              {currentSection + 1} / {totalSections}
            </div>

            {/* Warning Message */}
            {showWarning && (
              <Alert variant="danger">
                {t("surveyAnswerPage.error.answerAll")}
              </Alert>
            )}

            <div className="d-flex justify-content-between gap-2">
              <Button
                variant="secondary"
                disabled={currentSection === 0}
                onClick={() => setCurrentSection((prev) => prev - 1)}
              >
                {t("buttons.prev")}
              </Button>

              {currentSection < totalSections - 1 && (
                <Button variant="primary" onClick={handleNext}>
                  {t("buttons.next")}
                </Button>
              )}

              {currentSection === totalSections - 1 && (
                <Button
                  type="submit"
                  disabled={submitSurveyMutation.isPending || isAnswered}
                  className="btn btn-success"
                >
                  {submitSurveyMutation.isPending ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                    </>
                  ) : (
                    t("buttons.save")
                  )}
                </Button>
              )}
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

function useIntialSurveyValues(data?: Survey) {
  const isAnswered = !!data?.user_answers

  return useMemo(() => {
    if (!data) {
      return { answers: [], note: "" } as SurveySubmitData
    }
    if (isAnswered) {
      const noteQuestionId = data?.sections?.at(-1)?.questions?.at(-1)?.id

      const values = {
        note:
          data?.user_answers?.find(
            (ans) => ans?.survey_question_id === noteQuestionId
          )?.answer?.[0] || "",
        answers:
          data?.user_answers?.map((ans) => ({
            survey_question_id: ans?.survey_question_id,
            answer: ans?.answer?.[0] || "",
          })) || [],
      }
      return values as SurveySubmitData
    } else {
      return { answers: [], note: "" } as SurveySubmitData
    }
  }, [data])
}
